// useProductFetching.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllProducts } from '../services/productService';
import logger, { Logger } from '../utils/logger';

// Product type definition (from original component)
interface Product {
  quantity: number;
  _id: string;
  name: string;
  description?: string;
  price: number;
  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    [key: string]: any;
  };
  images?: Array<{
    filePath: string;
    fileName: string;
  }>;
}

interface UseProductFetchingProps {
  isOpen: boolean;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  paginationMode: 'paged' | 'infinite';
  excludeProductIds?: string[];
  selectedProductIds?: string[];
}

export const useProductFetching = ({
  isOpen,
  currentPage,
  pageSize,
  searchTerm,
  paginationMode,
  excludeProductIds = [],
  selectedProductIds = []
}: UseProductFetchingProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const loadingMoreRef = useRef<boolean>(false);
  
  // Track the last fetch parameters to prevent duplicate calls
  const lastFetchParams = useRef<string>('');

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    if (!isOpen) return;

    // Create a unique key for the current fetch params
    const paramsKey = JSON.stringify({
      page: currentPage,
      pageSize,
      searchTerm,
      paginationMode,
      excludeProductIds,
      selectedProductIds
    });

    // Skip if we're already loading or this exact request was just made
    if (loading || loadingMoreRef.current || paramsKey === lastFetchParams.current) {
      logger.log('⏭️ Skipping duplicate fetch request');
      return;
    }

    lastFetchParams.current = paramsKey;

    logger.log('📥 Fetching products:', {
      page: currentPage,
      searchTerm,
      mode: paginationMode,
      isLoading: loading,
      isLoadingMore: loadingMoreRef.current
    });

    try {
      setLoading(true);
      const params = {
        page: currentPage,
        perPage: pageSize,
        ...(searchTerm && { search: searchTerm.trim() }),
        orderBy: 'name',
        isAscending: true,
        ...(selectedProductIds?.length > 0 && { 
          selectedProducts: selectedProductIds.join(',') 
        })
      };
      
      const response = await getAllProducts(params);
      
      if (response?.data?.data) {
        // Filter out excluded product IDs
        const filteredProducts = response.data.data.filter(
          (product: Product) => !excludeProductIds.includes(product._id)
        );

        logger.log('✅ Fetched products:', filteredProducts.length);

        if (paginationMode === 'infinite' && currentPage > 1) {
          logger.log('🔄 Appending products for infinite scroll');
          setProducts(prev => [...prev, ...filteredProducts]);
        } else {
          logger.log('🔄 Replacing products for pagination');
          setProducts(filteredProducts);
        }
        
        setTotalProducts(response.data.total - excludeProductIds.length);
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error);
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
    }
  }, [isOpen, currentPage, pageSize, searchTerm, paginationMode, excludeProductIds, selectedProductIds, loading]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handle infinite scroll
  const handleScroll = useCallback((container: HTMLDivElement) => {
    if (paginationMode !== 'infinite' || loading || loadingMoreRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Check if user has scrolled to the bottom (with a threshold of 200px)
    if (scrollHeight - scrollTop - clientHeight < 200) {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil(totalProducts / pageSize);
      
      if (nextPage <= maxPage) {
        logger.log('📜 Loading next page:', nextPage);
        loadingMoreRef.current = true;
        return nextPage;
      }
    }
    
    return null;
  }, [currentPage, loading, totalProducts, pageSize, paginationMode]);

  return {
    products,
    loading,
    totalProducts,
    handleScroll,
    refetch: fetchProducts
  };
};